/**
 * GoodNewsWidget - AI-generated positive highlight messages
 *
 * Shows encouraging messages about content generation progress.
 * Cached: max 1 generation per user per site per hour.
 *
 * Features:
 * - Click to expand and see facts
 * - Link to content review
 * - Refresh button to generate new message
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  PartyPopper,
  Rocket,
  TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '@/utils/auth-fetch';
import { cn } from '@/lib/utils';

interface GoodNewsFacts {
  hasActivity: boolean;
  siteName?: string;
  generations24h?: number;
  published24h?: number;
  validated24h?: number;
  topCategory24h?: string;
  topCategoryCount?: number;
  featuredProduct?: string;
  featuredPrice?: string;
  pendingReview?: number;
  totalPublished?: number;
}

interface GoodNewsData {
  headline: string;
  message: string;
  featuredItem: string | null;
  facts: GoodNewsFacts;
  createdAt: string;
  isNew: boolean;
}

interface GoodNewsWidgetProps {
  siteId: string;
  className?: string;
}

const CELEBRATION_ICONS = [PartyPopper, Rocket, TrendingUp, Sparkles];

export function GoodNewsWidget({ siteId, className }: GoodNewsWidgetProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<GoodNewsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pick a random celebration icon for variety
  const [CelebrationIcon] = useState(() =>
    CELEBRATION_ICONS[Math.floor(Math.random() * CELEBRATION_ICONS.length)]
  );

  useEffect(() => {
    if (siteId) {
      fetchGoodNews();
    }
  }, [siteId]);

  const fetchGoodNews = async (forceNew = false) => {
    try {
      if (forceNew) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const url = forceNew
        ? `/good-news?siteId=${siteId}&forceNew=true`
        : `/good-news?siteId=${siteId}`;

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch good news');
      }

      const result = await response.json();
      if (result.success) {
        setData(result.data);
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (err) {
      console.error('[GoodNewsWidget] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchGoodNews(true);
  };

  const handleGoToContentReview = () => {
    navigate('/content-review');
  };

  if (loading) {
    return (
      <Card className={cn('bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20', className)}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return null; // Silently hide on error - this is optional content
  }

  const facts = data.facts;

  return (
    <Card className={cn(
      'bg-gradient-to-r from-primary/5 via-primary/10 to-emerald-500/10 border-primary/20 overflow-hidden transition-all duration-300',
      expanded && 'shadow-lg',
      className
    )}>
      <CardContent className="p-4">
        {/* Main Content */}
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 p-2 rounded-full bg-primary/10">
            <CelebrationIcon className="h-6 w-6 text-primary" />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-foreground truncate">
                {data.headline}
              </h3>
              {data.isNew && (
                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-primary/20 text-primary rounded">
                  NEW
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {data.message}
            </p>

            {/* Featured Item Badge */}
            {data.featuredItem && (
              <div className="mt-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-full">
                  <Sparkles className="h-3 w-3" />
                  {data.featuredItem}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex-shrink-0 flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="h-8 w-8 p-0"
              title="Generate new message"
            >
              <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="h-8 w-8 p-0"
              title={expanded ? 'Show less' : 'Show more'}
            >
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Expanded Content - Facts & Actions */}
        {expanded && facts && (
          <div className="mt-4 pt-4 border-t border-primary/10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {facts.generations24h !== undefined && facts.generations24h > 0 && (
                <div className="text-center p-2 rounded-lg bg-background/50">
                  <div className="text-lg font-bold text-foreground">{facts.generations24h}</div>
                  <div className="text-xs text-muted-foreground">Generated (24h)</div>
                </div>
              )}
              {facts.published24h !== undefined && facts.published24h > 0 && (
                <div className="text-center p-2 rounded-lg bg-background/50">
                  <div className="text-lg font-bold text-emerald-600">{facts.published24h}</div>
                  <div className="text-xs text-muted-foreground">Published (24h)</div>
                </div>
              )}
              {facts.pendingReview !== undefined && facts.pendingReview > 0 && (
                <div className="text-center p-2 rounded-lg bg-background/50">
                  <div className="text-lg font-bold text-amber-600">{facts.pendingReview}</div>
                  <div className="text-xs text-muted-foreground">Pending Review</div>
                </div>
              )}
              {facts.totalPublished !== undefined && facts.totalPublished > 0 && (
                <div className="text-center p-2 rounded-lg bg-background/50">
                  <div className="text-lg font-bold text-foreground">{facts.totalPublished}</div>
                  <div className="text-xs text-muted-foreground">Total Published</div>
                </div>
              )}
            </div>

            {/* Top Category */}
            {facts.topCategory24h && (
              <div className="mb-4 p-2 rounded-lg bg-background/50 text-sm">
                <span className="text-muted-foreground">Top category today: </span>
                <span className="font-medium text-foreground">{facts.topCategory24h}</span>
                {facts.topCategoryCount && facts.topCategoryCount > 0 && (
                  <span className="text-muted-foreground"> ({facts.topCategoryCount} items)</span>
                )}
              </div>
            )}

            {/* Action Button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleGoToContentReview}
                className="gap-2"
              >
                Go to Content Review
                <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default GoodNewsWidget;
